import axios, { AxiosHeaders, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import { getAccessToken, useAuthStore } from '@/store/authStore';
import { normalizeError } from './errors';

/** Endpoints that must never trigger an automatic refresh-retry loop. */
const NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/login/mfa',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/accept-invitation',
];

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/** Base instance shared by the whole app. `withCredentials` sends the refresh cookie. */
export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

/**
 * Bare instance used only to rotate the refresh token. It has NO interceptors, so
 * a failed refresh cannot recurse back into the refresh logic.
 */
const refreshClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
  timeout: 30_000,
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

// --- Single-flight refresh -----------------------------------------------------
let refreshPromise: Promise<string | null> | null = null;

async function rotateAccessToken(): Promise<string | null> {
  try {
    const response = await refreshClient.post<{ data: { accessToken: string; expiresIn: number } }>(
      '/auth/refresh',
      {},
    );
    const { accessToken, expiresIn } = response.data.data;
    useAuthStore.getState().setAccessToken(accessToken, expiresIn);
    return accessToken;
  } catch {
    return null;
  }
}

/** Obtain a fresh access token, coalescing concurrent callers into one request. */
export function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= rotateAccessToken().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.response || !error.config) {
      return Promise.reject(normalizeError(error));
    }

    const config = error.config as RetryableConfig;
    const url = config.url ?? '';
    const skipRefresh = NO_REFRESH_PATHS.some((path) => url.includes(path));

    if (error.response.status === 401 && !config._retry && !skipRefresh) {
      config._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        const headers = AxiosHeaders.from(config.headers);
        headers.set('Authorization', `Bearer ${newToken}`);
        config.headers = headers;
        return apiClient.request(config);
      }
      // Refresh failed => the session is truly gone. Surface a clean expiry.
      useAuthStore.getState().clear('expired');
    }

    return Promise.reject(normalizeError(error));
  },
);
